import { useEffect, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import SpecificationTab from "./Tabs/SpecificationTab";
import DescriptionTab from "./Tabs/DescriptionTab";
import DocumentsTab from "./Tabs/DocumentsTab";
import styles from "./DescriptionTabs.module.scss";
import { useTranslation } from "react-i18next";

const CustomTabPanel = ({ children, value, index, ...other }) => {
    return (
        <div
            // style={{ fontFamily: "Montserrat !important" }}
            role="tabpanel"
            className={styles.panel}
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <div className={styles.panelBody}>{children}</div>
            )}
        </div>
    );
};

const a11yProps = (index) => {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
};

const CUSTOM_TAB_PREFIX = "product_details_tab_";
const DEFAULT_TAB_ORDER = ["specification", "additional_info", "documentation"];

// A custom tab's "_name" position is fetched through the same specification
// endpoint/tree shape as any other attribute position - walk it and pull out
// the first attribute value found, to use as the tab's displayed title.
const extractSpecificationTitle = (tree) => {
    for (const set of tree ?? []) {
        for (const group of Object.values(set?.groups ?? {})) {
            for (const attribute of Object.values(group?.attributes ?? {})) {
                const value = (attribute?.values ?? [])[0]?.name;
                if (value) return value;
            }
        }
    }
    return null;
};

// "html" display mode: instead of a name/value list, flatten every attribute
// value assigned to the position into one flowing block, in the same order the
// backend already returns them (order_attribute). Each value can itself contain
// real HTML (bold, line breaks, links, ...) since it's rendered the same way
// admin-authored content already is elsewhere in this app (StaticPage.js,
// DescriptionTab.jsx) - so a longer passage is composed by adding several
// attributes to the position rather than needing one huge field.
const CustomTabHtmlContent = ({ specification }) => {
    const values = (specification ?? [])
        .flatMap((set) => Object.values(set?.groups ?? {}))
        .flatMap((group) => Object.values(group?.attributes ?? {}))
        .flatMap((attribute) => (attribute?.values ?? []).map((value) => value.name))
        .filter(Boolean);

    return (
        <Box className={styles.tabContent} sx={{ fontFamily: "Montserrat !important" }}>
            {values.map((html, index) => (
                <div key={index} className={styles.htmlBlock} dangerouslySetInnerHTML={{ __html: html }} />
            ))}
        </Box>
    );
};

// "group_list" / "set_group_list" display modes: same name/value rows as the default
// list, but with a heading per group (and, for set_group_list, a more prominent outer
// heading per set) instead of one flat list - for content that's naturally organized
// into several named sections. Reuses the same .tabTitle/.tabSection pattern already
// used for section headings elsewhere in this tab area (DescriptionTab, DocumentsTab),
// so a group heading looks consistent with the rest of the page rather than borrowing
// the mobile-accordion-only .mobileTitle style.
const CustomTabGroupedList = ({ specification, showSetTitle }) => (
    <Box className={styles.tabContent} sx={{ fontFamily: "Montserrat !important" }}>
        {(specification ?? []).map((data) => (
            <Box key={data?.set?.id} className={showSetTitle ? styles.setSection : undefined}>
                {showSetTitle && data?.set?.name && <h5 className={styles.setTitle} dangerouslySetInnerHTML={{ __html: data.set.name }} />}
                {Object.values(data?.groups ?? {}).map((item) => (
                    <Box key={item?.group?.id} className={styles.tabSection}>
                        {item?.group?.name && <h6 className={styles.tabTitle} dangerouslySetInnerHTML={{ __html: item.group.name }} />}
                        {Object.values(item?.attributes ?? {}).map((attribute) => {
                            const valuesHtml = (attribute?.values ?? []).map((value) => value.name).join(", ");

                            return (
                                <p key={attribute?.attribute?.id} className={styles.specificationRow}>
                                    <span className={styles.specificationRowTitle} dangerouslySetInnerHTML={{ __html: attribute?.attribute?.name }} />
                                    <span className={styles.specificationRowValue} dangerouslySetInnerHTML={{ __html: valuesHtml }} />
                                </p>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        ))}
    </Box>
);

// "html_with_titles" display mode: like the plain HTML mode, but each attribute's own
// name is shown as a heading above its flattened values - for content that's several
// distinct named blocks of prose rather than one continuous block.
const CustomTabHtmlContentWithTitles = ({ specification }) => {
    const attributes = (specification ?? []).flatMap((set) => Object.values(set?.groups ?? {})).flatMap((group) => Object.values(group?.attributes ?? {}));

    return (
        <Box className={styles.tabContent} sx={{ fontFamily: "Montserrat !important" }}>
            {attributes.map((attribute) => {
                const values = (attribute?.values ?? []).map((value) => value.name).filter(Boolean);
                if (values.length === 0) return null;

                return (
                    <Box key={attribute?.attribute?.id} className={styles.tabSection}>
                        <h6 className={styles.tabTitle} dangerouslySetInnerHTML={{ __html: attribute?.attribute?.name }} />
                        {values.map((html, index) => (
                            <div key={index} className={styles.htmlBlock} dangerouslySetInnerHTML={{ __html: html }} />
                        ))}
                    </Box>
                );
            })}
        </Box>
    );
};

// Maps a CMS-configured "tab_N_display_mode" value to the component that renders a
// custom tab's fetched specification tree. "list" isn't listed here - it's the
// fallback (see tabsToShow below) so an unrecognized/future mode degrades safely
// to today's default rather than rendering nothing.
const CUSTOM_TAB_RENDERERS = {
    html: (specification) => <CustomTabHtmlContent specification={specification} />,
    group_list: (specification) => <CustomTabGroupedList specification={specification} showSetTitle={false} />,
    set_group_list: (specification) => <CustomTabGroupedList specification={specification} showSetTitle={true} />,
    html_with_titles: (specification) => <CustomTabHtmlContentWithTitles specification={specification} />,
};

const DescriptionTabs = ({ product, translateKey }) => {
    const { t } = useTranslation();

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const isDecleration = Object.values(product?.declaration).some((dec) => dec != null && dec !== "");

    // Remove not used keys from description object
    const { short_description, ...productDescription } = product?.description ?? {};
    const isDescription = Object.values(productDescription).some((des) => des != null && des !== "");

    // The 3 built-in tabs. Admin config only ever decides which keys appear and in what
    // order (see below) - each tab's own "condition" (does it actually have data) is
    // computed here exactly as before, and always wins: an admin-enabled tab with no
    // data still never renders.
    const SYSTEM_TAB_DEFINITIONS = {
        specification: {
            label: t(`${translateKey}-text-specification`),
            condition: product?.specification.length > 0,
            panel: <SpecificationTab specification={product.specification} />,
        },
        additional_info: {
            label: t(`${translateKey}-text-additional_info`),
            condition: isDecleration || isDescription,
            panel: <DescriptionTab translateKey={translateKey} description={product.description} declaration={product.declaration} />,
        },
        documentation: {
            label: t(`${translateKey}-text-documentation`),
            condition: product?.technicalDoc.length > 0 || product?.instructionDoc.length > 0 || product?.certificateDoc.length > 0,
            panel: <DocumentsTab translateKey={translateKey} technicalDoc={product.technicalDoc} instructionDoc={product.instructionDoc} certificateDoc={product.certificateDoc} />,
        },
    };

    // Falls back to the original fixed order whenever no CMS tab config has been saved
    // yet, or it failed to load - reproduces today's exact behavior either way.
    const orderedTabKeys = product?.orderedTabKeys?.length ? product.orderedTabKeys : DEFAULT_TAB_ORDER;

    const tabsToShow = orderedTabKeys
        .map((key) => {
            if (key.startsWith(CUSTOM_TAB_PREFIX)) {
                const slotData = product?.customTabsData?.[key];
                const content = slotData?.content ?? [];
                const slotNumber = key.replace(CUSTOM_TAB_PREFIX, "");

                const renderer = CUSTOM_TAB_RENDERERS[slotData?.displayMode];

                return {
                    key,
                    label: extractSpecificationTitle(slotData?.name) || `Tab ${slotNumber}`,
                    condition: content.length > 0,
                    panel: renderer ? renderer(content) : <SpecificationTab specification={content} />,
                };
            }

            return SYSTEM_TAB_DEFINITIONS[key] ? { key, ...SYSTEM_TAB_DEFINITIONS[key] } : null;
        })
        .filter(Boolean);

    const index = tabsToShow.findIndex((item) => item.condition === true);

    const [value, setValue] = useState(index);

    useEffect(() => {
        const index = tabsToShow.findIndex((value) => value.value);
        if (index !== -1 && value !== index) {
            setValue(index);
        }
    }, []);
    return (
        <Box className={styles.tabsWrapper} sx={{ width: "100%" }}>
            <Box>
                <Box>
                    <Tabs
                        className={styles.tabBar}
                        value={value}
                        onChange={handleChange}
                        variant="scrollable"
                        scrollButtons={false}
                        aria-label="Informacije o proizvodu"
                        TabIndicatorProps={{ style: { display: "none" } }}
                    >
                        {tabsToShow.map((tab, index) => {
                            if (tab.condition) {
                                return <Tab key={tab.key} label={tab.label} {...a11yProps(index)} className={styles.tab} />;
                            } else {
                                return <Tab key={tab.key} label={""} {...a11yProps(index)} className={styles.hideTab} />;
                            }
                        })}
                    </Tabs>
                </Box>
                {tabsToShow.map((tab, index) => (
                    <CustomTabPanel key={tab.key} value={value} index={index}>
                        {tab.panel}
                    </CustomTabPanel>
                ))}
            </Box>

        </Box>
    );
};

export default DescriptionTabs;
